import React from 'react'
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet'
import { getEquipmentOptions, createRoomMultipart, updateRoomMultipart } from '../api/rooms'
import { useTranslation } from 'react-i18next' // Import de useTranslation

// NOTE: Pour les options (select), on garde souvent les valeurs techniques 'Furnished' 
// mais on traduit les labels à l'affichage. Ici, je le fais dynamiquement dans le render.
const TYPES = [
  { value: 'Furnished', labelKey: 'roomType.furnished' },
  { value: 'Studio', labelKey: 'roomType.studio' },
  { value: 'Appartement', labelKey: 'roomType.appartement' },
  { value: 'Hotel', labelKey: 'roomType.hotel' },
  { value: 'Villa', labelKey: 'roomType.villa' },
  { value: 'ForRent', labelKey: 'roomType.forRent' },
  { value: 'TwoRoom', labelKey: 'roomType.twoRoom' },
  { value: 'SharedRoom', labelKey: 'roomType.sharedRoom' },
  { value: 'House', labelKey: 'roomType.house' },
  { value: 'Parking', labelKey: 'roomType.parking' },
]

const PRICE_UNITS = [
  { value: 'PerNight', labelKey: 'priceUnit.perNight' },
  { value: 'PerWeek', labelKey: 'priceUnit.perWeek' },
  { value: 'PerMonth', labelKey: 'priceUnit.perMonth' },
]

function ClickToSetMarker({ onPick }) {
  useMapEvents({
    click(e) { onPick({ lat: e.latlng.lat, lng: e.latlng.lng }) },
  })
  return null
}

async function reverseGeocode(lat, lon) {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}`
    // On pourrait passer la langue actuelle ici si on veut les adresses traduites
    const res = await fetch(url, { headers: { 'Accept-Language': 'fr' } })
    const data = await res.json()
    const a = data?.address || {}
    return {
      country: a.country || '',
      city: a.city || a.town || a.village || a.municipality || '',
      district: a.suburb || a.neighbourhood || a.city_district || '',
      street: [a.road, a.house_number].filter(Boolean).join(' ').trim(),
    }
  } catch { return {} }
}

export default function RoomForm({ mode = 'create', initial, onDone }) {
  const { t } = useTranslation() // Hook de traduction

  const [equipments, setEquipments] = React.useState([])
  const [newImages, setNewImages] = React.useState([])
  const existing = React.useMemo(() => {
    const arr = Array.isArray(initial?.imageUrl) ? initial.imageUrl : (initial?.images || [])
    return arr.filter(Boolean)
  }, [initial])
  const [preview, setPreview] = React.useState([])
  const [err, setErr] = React.useState(null)
  const [loading, setLoading] = React.useState(false)

  const [marker, setMarker] = React.useState(
    initial?.latitude && initial?.longitude
      ? { lat: Number(initial.latitude), lng: Number(initial.longitude) }
      : { lat: 3.848, lng: 11.502 } 
  )

  const [f, setF] = React.useState({
    name: initial?.name || '',
    type: initial?.type || 'Furnished',
    description: initial?.description || '',
    availableFrom: initial?.availableFrom?.slice?.(0,10) || '',
    availableTo: initial?.availableTo?.slice?.(0,10) || '',
    latitude: initial?.latitude || '',
    longitude: initial?.longitude || '',
    
    priceUnit: initial?.price?.unit || initial?.priceUnit || 'PerNight',
    priceAmount: initial?.price?.amount ?? initial?.priceAmount ?? '',
    priceCurrency: initial?.price?.currency || initial?.priceCurrency || 'XAF',
    
    locationCountry: initial?.location?.country || initial?.locationCountry || 'Cameroon',
    locationCity: initial?.location?.city || initial?.locationCity || '',
    locationDistrict: initial?.location?.district || initial?.locationDistrict || '',
    locationStreet: initial?.location?.street || initial?.locationStreet || '',
    
    equipmentIds: initial?.equipmentIds || [],
    
    roomsCount: initial?.roomsCount ?? '',
    sizeInSquareMeters: initial?.sizeInSquareMeters ?? '',
    maxGuests: initial?.maxGuests ?? '',
    bedCount: initial?.bedCount ?? '',
    hasPrivateBathroom: !!initial?.hasPrivateBathroom,
    cleaningFee: initial?.cleaningFee ?? '',
    minimumStayInNights: initial?.minimumStayInNights ?? '',
    maximumStayInNights: initial?.maximumStayInNights ?? '',
    isNegotiable: !!initial?.isNegotiable,
  })
  
  const up = (k, v) => setF(s => ({ ...s, [k]: v }))

  React.useEffect(() => { getEquipmentOptions().then(setEquipments).catch(console.error) }, [])

  const onPickMap = async ({ lat, lng }) => {
    setMarker({ lat, lng })
    up('latitude', lat.toFixed(6))
    up('longitude', lng.toFixed(6))
    const addr = await reverseGeocode(lat, lng)
    if (addr.country) up('locationCountry', addr.country)
    if (addr.city) up('locationCity', addr.city)
    if (addr.district) up('locationDistrict', addr.district)
    if (addr.street) up('locationStreet', addr.street)
  }

  const onFiles = (e) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    setNewImages(prev => [...prev, ...files])
    setPreview(prev => [...prev, ...files.map(f => URL.createObjectURL(f))])
    e.target.value = '' 
  }

  const removeNewAt = (index) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
    setPreview(prev => prev.filter((_, i) => i !== index));
  }

  const toggleEquip = (id) => {
    setF(s => {
      const idInt = parseInt(id, 10);
      const set = new Set(s.equipmentIds)
      set.has(idInt) ? set.delete(idInt) : set.add(idInt)
      return { ...s, equipmentIds: Array.from(set) }
    })
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setErr(null)
    if (!f.name || !f.type || !f.description) return setErr(t('form.error.requiredFields', 'Nom, type et description sont obligatoires.'))
    if (!f.priceUnit) return setErr(t('form.error.priceUnit', 'L’unité de prix est obligatoire.'))
    if (!f.locationCountry || !f.locationCity) return setErr(t('form.error.location', 'Pays et ville sont obligatoires.'))

    setLoading(true)
    try {
      const payload = { ...f, images: newImages }
      if (mode === 'edit' && initial?.id) await updateRoomMultipart(initial.id, payload)
      else await createRoomMultipart(payload)
      onDone?.()
    } catch (e2) {
      console.error(e2)
      setErr(t('form.error.saveFailed', 'Échec d’enregistrement. Vérifiez la console.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-card p-5">
      <div className="mb-5">
        <h2 className="text-2xl font-extrabold">
          {mode === 'edit' ? t('form.title.edit', 'Modifier l’annonce') : t('form.title.new', 'Publier un bien')}
        </h2>
        <p className="text-muted" dangerouslySetInnerHTML={{ __html: t('form.subtitle', 'Renseigne les informations et clique sur <strong>Publier</strong>.') }} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Colonne Gauche */}
        <div className="grid gap-4">
          <div className="grid gap-1">
            <label className="font-semibold">{t('form.label.name', 'Nom')} *</label>
            <input className="px-3 py-2 rounded-xl border border-blue-100" value={f.name} onChange={e=>up('name', e.target.value)} />
          </div>

          <div className="grid gap-1">
            <label className="font-semibold">{t('form.label.type', 'Type')} *</label>
            <select className="px-3 py-2 rounded-xl border border-blue-100" value={f.type} onChange={e=>up('type', e.target.value)}>
              {TYPES.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey, opt.value)}
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-1">
            <label className="font-semibold">{t('form.label.description', 'Description')} *</label>
            <textarea className="px-3 py-2 rounded-xl border border-blue-100 min-h-[110px]" value={f.description} onChange={e=>up('description', e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="grid gap-1">
              <span className="font-semibold">{t('form.label.availableFrom', 'Disponible du')}</span>
              <input type="date" className="px-3 py-2 rounded-xl border border-blue-100" value={f.availableFrom} onChange={e=>up('availableFrom', e.target.value)} />
            </label>
            <label className="grid gap-1">
              <span className="font-semibold">{t('form.label.availableTo', 'au')}</span>
              <input type="date" className="px-3 py-2 rounded-xl border border-blue-100" value={f.availableTo} onChange={e=>up('availableTo', e.target.value)} />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="grid gap-1">
              <span className="font-semibold">{t('form.label.priceAmount', 'Prix (montant)')}</span>
              <input type="number" className="px-3 py-2 rounded-xl border border-blue-100" value={f.priceAmount} onChange={e=>up('priceAmount', e.target.value)} />
            </label>
            <label className="grid gap-1">
              <span className="font-semibold">{t('form.label.priceUnit', 'Unité')} *</span>
              <select className="px-3 py-2 rounded-xl border border-blue-100" value={f.priceUnit} onChange={e=>up('priceUnit', e.target.value)}>
                {PRICE_UNITS.map(u => (
                  <option key={u.value} value={u.value}>
                    {t(u.labelKey, u.value)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="grid gap-1">
              <span className="font-semibold">{t('form.label.currency', 'Monnaie')}</span>
              <input className="px-3 py-2 rounded-xl border border-blue-100" value={f.priceCurrency} onChange={e=>up('priceCurrency', e.target.value)} />
            </label>
            <label className="grid gap-1">
              <span className="font-semibold">{t('form.label.cleaningFee', 'Frais de ménage')}</span>
              <input type="number" className="px-3 py-2 rounded-xl border border-blue-100" value={f.cleaningFee} onChange={e=>up('cleaningFee', e.target.value)} />
            </label>
          </div>

          {/* Équipements */}
          <label className="grid gap-1">
            <span className="font-semibold">{t('form.label.equipments', 'Équipements')}</span>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-auto p-2 border rounded-xl bg-gray-50">
              {equipments.map(e => (
                <label key={e.id} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={f.equipmentIds.includes(e.id)}
                    onChange={() => toggleEquip(e.id)}
                  />
                  {/* On suppose que les noms d'équipements viennent du back, mais on pourrait aussi les traduire si on avait des clés */}
                  <span className="text-sm">{e.name}</span>
                </label>
              ))}
            </div>
          </label>

          {/* Images */}
          <label className="grid gap-1">
            <span className="font-semibold">{t('form.label.photos', 'Photos (plusieurs)')}</span>
            <input type="file" accept="image/*" multiple onChange={onFiles} className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
            
            <div className="flex gap-2 flex-wrap mt-2">
                {existing.map((src,i)=>(
                    <img key={`ex-${i}`} src={src} alt="" className="w-24 h-24 object-cover rounded-lg border border-gray-300" />
                ))}
                {preview.map((src,i)=>(
                    <div key={`nv-${i}`} className="relative">
                        <img src={src} alt="" className="w-24 h-24 object-cover rounded-lg border border-blue-300" />
                        <button type="button" onClick={()=>removeNewAt(i)} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-600 text-white text-sm flex items-center justify-center">×</button>
                    </div>
                ))}
            </div>
          </label>
        </div>

        {/* Colonne Droite */}
        <div className="grid gap-4">
          <label className="grid gap-1">
            <span className="font-semibold">{t('form.label.map', 'Localiser sur la carte')}</span>
            <div className="rounded-xl overflow-hidden border z-0">
              <MapContainer center={[marker.lat, marker.lng]} zoom={6} style={{ height: 370 }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <ClickToSetMarker onPick={onPickMap} />
                <Marker position={[marker.lat, marker.lng]} />
              </MapContainer>
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('form.hint.map', 'Cliquez sur la carte pour pré-remplir l\'adresse.')}</p>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="grid gap-1">
              <span className="font-semibold">{t('form.label.country', 'Pays')} *</span>
              <input className="px-3 py-2 rounded-xl border border-blue-100" value={f.locationCountry} onChange={e=>up('locationCountry', e.target.value)} />
            </label>
            <label className="grid gap-1">
              <span className="font-semibold">{t('form.label.city', 'Ville')} *</span>
              <input className="px-3 py-2 rounded-xl border border-blue-100" value={f.locationCity} onChange={e=>up('locationCity', e.target.value)} />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="grid gap-1">
              <span className="font-semibold">{t('form.label.district', 'Quartier')}</span>
              <input className="px-3 py-2 rounded-xl border border-blue-100" value={f.locationDistrict} onChange={e=>up('locationDistrict', e.target.value)} />
            </label>
            <label className="grid gap-1">
              <span className="font-semibold">{t('form.label.street', 'Rue')}</span>
              <input className="px-3 py-2 rounded-xl border border-blue-100" value={f.locationStreet} onChange={e=>up('locationStreet', e.target.value)} />
            </label>
          </div>

          {/* Détails Numériques */}
          <div className="grid grid-cols-2 gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
            <label className="grid gap-1">
              <span className="font-semibold text-sm">{t('form.label.roomsCount', 'Pièces')}</span>
              <input type="number" className="px-3 py-2 rounded-lg border border-gray-200" value={f.roomsCount} onChange={e=>up('roomsCount', e.target.value)} />
            </label>
            <label className="grid gap-1">
              <span className="font-semibold text-sm">{t('form.label.size', 'Surface (m²)')}</span>
              <input type="number" className="px-3 py-2 rounded-lg border border-gray-200" value={f.sizeInSquareMeters} onChange={e=>up('sizeInSquareMeters', e.target.value)} />
            </label>
            <label className="grid gap-1">
              <span className="font-semibold text-sm">{t('form.label.maxGuests', 'Invités max')}</span>
              <input type="number" className="px-3 py-2 rounded-lg border border-gray-200" value={f.maxGuests} onChange={e=>up('maxGuests', e.target.value)} />
            </label>
            <label className="grid gap-1">
              <span className="font-semibold text-sm">{t('form.label.bedCount', 'Lits')}</span>
              <input type="number" className="px-3 py-2 rounded-lg border border-gray-200" value={f.bedCount} onChange={e=>up('bedCount', e.target.value)} />
            </label>
            <label className="grid gap-1">
              <span className="font-semibold text-sm">{t('form.label.minStay', 'Séjour Min (nuits)')}</span>
              <input type="number" className="px-3 py-2 rounded-lg border border-gray-200" value={f.minimumStayInNights} onChange={e=>up('minimumStayInNights', e.target.value)} />
            </label>
            <label className="grid gap-1">
              <span className="font-semibold text-sm">{t('form.label.maxStay', 'Séjour Max (nuits)')}</span>
              <input type="number" className="px-3 py-2 rounded-lg border border-gray-200" value={f.maximumStayInNights} onChange={e=>up('maximumStayInNights', e.target.value)} />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="w-5 h-5 text-blue-600" checked={f.hasPrivateBathroom} onChange={e=>up('hasPrivateBathroom', e.target.checked)} />
              <span className="font-medium">{t('form.label.privateBathroom', 'Salle de bain privée')}</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="w-5 h-5 text-blue-600" checked={f.isNegotiable} onChange={e=>up('isNegotiable', e.target.checked)} />
              <span className="font-medium">{t('form.label.negotiable', 'Prix négociable')}</span>
            </label>
          </div>

          {err && <div className="text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</div>}

          {/* bouton fixe */}
          <div className="sticky bottom-0 bg-white/80 backdrop-blur border-t border-blue-100 p-3 rounded-b-xl">
            <button type="submit" disabled={loading} className="w-full px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold transition shadow-md">
              {loading 
                ? (mode === 'edit' ? t('form.btn.updating', 'Mise à jour…') : t('form.btn.publishing', 'Publication…')) 
                : (mode === 'edit' ? t('form.btn.update', 'Mettre à jour') : t('form.btn.publish', 'Publier'))
              }
            </button>
          </div>
        </div>
      </div>
    </form>
  )
}